import * as T from 'three';
import { surfaceAt, type RideTrack } from '../model';
import type { RideCourse } from '../course';
import type { Quality } from './quality';
import { makeNoise, noiseTexture } from './noise';
import { loadLayer, proceduralLayer, type Layer } from './textures';

const ratio = (a: string, b: string) => { const x = new T.Color(a), y = new T.Color(b); return new T.Color(Math.min(2, x.r / Math.max(.02, y.r)), Math.min(2, x.g / Math.max(.02, y.g)), Math.min(2, x.b / Math.max(.02, y.b))); };
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

export interface Projection { d: number; l: number; dist: number; y: number; tx: number; tz: number; interior: boolean }

/**
 * The course as a dense polyline (with straight run-in/run-out extensions on open trails)
 * and a spatial hash, so any world point can be expressed as (distance along, lateral).
 */
export class TrackField {
  readonly closed: boolean; readonly ext: number;
  xs: Float32Array; zs: Float32Array; ys: Float32Array; ds: Float32Array; count: number;
  private cells = new Map<number, number[]>(); private cell = 16; private reach = 52; private coarse: number[] = [];
  minX = Infinity; maxX = -Infinity; minZ = Infinity; maxZ = -Infinity;
  constructor(readonly track: RideTrack, readonly course: RideCourse, step = 2) {
    const a = course.sample(0), b = course.sample(track.length);
    this.closed = Math.hypot(a.x - b.x, a.z - b.z) < 1; this.ext = this.closed ? 0 : 70;
    const points: number[][] = [];
    for (let d = -this.ext; d <= track.length + this.ext + 1e-6; d += step) { const c = this.center(Math.min(d, track.length + this.ext)); points.push([c.x, c.z, c.y, d]); }
    this.count = points.length;
    this.xs = Float32Array.from(points, p => p[0]); this.zs = Float32Array.from(points, p => p[1]); this.ys = Float32Array.from(points, p => p[2]); this.ds = Float32Array.from(points, p => p[3]);
    for (let i = 0; i < this.count; i++) { this.minX = Math.min(this.minX, this.xs[i]); this.maxX = Math.max(this.maxX, this.xs[i]); this.minZ = Math.min(this.minZ, this.zs[i]); this.maxZ = Math.max(this.maxZ, this.zs[i]); }
    for (let i = 0; i < this.count - 1; i++) {
      const x0 = Math.min(this.xs[i], this.xs[i + 1]) - this.reach, x1 = Math.max(this.xs[i], this.xs[i + 1]) + this.reach;
      const z0 = Math.min(this.zs[i], this.zs[i + 1]) - this.reach, z1 = Math.max(this.zs[i], this.zs[i + 1]) + this.reach;
      for (let cx = Math.floor(x0 / this.cell); cx <= Math.floor(x1 / this.cell); cx++) for (let cz = Math.floor(z0 / this.cell); cz <= Math.floor(z1 / this.cell); cz++) {
        const key = (cx + 32768) * 65536 + cz + 32768; const list = this.cells.get(key); if (list) list.push(i); else this.cells.set(key, [i]);
      }
    }
    for (let i = 0; i < this.count - 1; i += 6) this.coarse.push(i);
    if (this.coarse[this.coarse.length - 1] !== this.count - 2) this.coarse.push(this.count - 2);
  }
  /**
   * Trail centre (x, y) as a continuous function of z. Open trails run monotonically along z,
   * so this gives the valley floor everywhere, without the seams a nearest-point lookup has.
   */
  valley(z: number) {
    const zs = this.zs, n = this.count;
    if (z <= zs[0]) return { x: this.xs[0], y: this.ys[0] };
    if (z >= zs[n - 1]) return { x: this.xs[n - 1], y: this.ys[n - 1] };
    let lo = 0, hi = n - 1;
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (zs[mid] < z) lo = mid; else hi = mid; }
    const t = (z - zs[lo]) / Math.max(1e-6, zs[hi] - zs[lo]);
    return { x: this.xs[lo] + (this.xs[hi] - this.xs[lo]) * t, y: this.ys[lo] + (this.ys[hi] - this.ys[lo]) * t };
  }
  /** Centre line including straight extensions beyond the ends of an open trail. */
  center(d: number) {
    const L = this.track.length;
    if (d >= 0 && d <= L) { const p = this.course.sample(d); return { x: p.x, y: p.y, z: p.z, tx: p.tx, tz: p.tz }; }
    const end = this.course.sample(d < 0 ? 0 : L), over = d < 0 ? d : d - L;
    return { x: end.x + end.tx * over, y: end.y, z: end.z + end.tz * over, tx: end.tx, tz: end.tz };
  }
  private segment(i: number, x: number, z: number, out: Projection, best: number) {
    const ax = this.xs[i], az = this.zs[i], dx = this.xs[i + 1] - ax, dz = this.zs[i + 1] - az, len2 = dx * dx + dz * dz || 1e-6;
    const t = Math.min(1, Math.max(0, ((x - ax) * dx + (z - az) * dz) / len2));
    const px = ax + dx * t, pz = az + dz * t, dist2 = (x - px) ** 2 + (z - pz) ** 2;
    if (dist2 >= best) return best;
    const len = Math.sqrt(len2), tx = dx / len, tz = dz / len;
    out.d = this.ds[i] + (this.ds[i + 1] - this.ds[i]) * t; out.l = (x - px) * -tz + (z - pz) * tx; out.dist = Math.sqrt(dist2);
    out.y = this.ys[i] + (this.ys[i + 1] - this.ys[i]) * t; out.tx = tx; out.tz = tz;
    out.interior = this.closed || !(i === 0 && t <= 0 || i === this.count - 2 && t >= 1);
    return dist2;
  }
  project(x: number, z: number, out: Projection): Projection {
    let best = Infinity;
    const list = this.cells.get((Math.floor(x / this.cell) + 32768) * 65536 + Math.floor(z / this.cell) + 32768);
    if (list) for (const i of list) best = this.segment(i, x, z, out, best);
    if (best === Infinity) for (const i of this.coarse) best = this.segment(i, x, z, out, best);
    return out;
  }
}

/** Per-biome look of the ground. Texture ids refer to public/assets/env/textures/<id>/. */
interface LayerSpec { tex: string; color: string; rough: number; scale: number; /** Multiplies photographic texture colour (ignored by the procedural stand-in). */ tint?: string; gain?: number }
interface GroundSpec {
  a: LayerSpec; b: LayerSpec; rock: LayerSpec; track: LayerSpec;
  bBias: number; rockSlope: number; snowLine: number; verge: number; ragged: number;
  gravel: string; loam: string; detail: number; tint: string;
  /** 0..1 strength of sun-dried patches across open ground. */
  dry: number;
}
const GROUNDS: Record<RideTrack['biome'], GroundSpec> = {
  alpine: { a: { tex: 'forrest_ground_01', color: '#4f5b33', rough: .92, scale: 3.6, tint: '#cfeab2' }, b: { tex: 'forest_leaves_02', color: '#5b4a33', rough: .9, scale: 3.4 }, rock: { tex: 'rock_face', color: '#8d8a83', rough: .82, scale: 9 }, track: { tex: 'rocky_trail', color: '#8b7a62', rough: .9, scale: 4.2 }, bBias: .02, rockSlope: .32, snowLine: 780, verge: .7, ragged: 1.1, gravel: '#c9c4ba', loam: '#5a4634', detail: 3.4, tint: '#ffffff', dry: 0.12 },
  canyon: { a: { tex: 'dry_ground_rocks', color: '#a35c3a', rough: .94, scale: 4.2, tint: '#ecc2ae' }, b: { tex: 'dirt_floor', color: '#c08a5e', rough: .95, scale: 4.5, tint: '#e8c4b0' }, rock: { tex: 'cliff_side', color: '#a8573a', rough: .88, scale: 14 }, track: { tex: 'red_dirt_mud_01', color: '#b06a42', rough: .93, scale: 4.5 }, bBias: -.05, rockSlope: .2, snowLine: 0, verge: .9, ragged: .9, gravel: '#c9a27f', loam: '#7d4a33', detail: 2.2, tint: '#ffffff', dry: 0.35 },
  coast: { a: { tex: 'dry_ground_rocks', color: '#8e8a58', rough: .95, scale: 4, tint: '#e8e6c4' }, b: { tex: 'forrest_ground_01', color: '#6f7d46', rough: .93, scale: 4.2, tint: '#f2e9c0' }, rock: { tex: 'rock_face', color: '#a59d8c', rough: .85, scale: 9 }, track: { tex: 'clean_asphalt', color: '#45474a', rough: .78, scale: 5.5, gain: 2.3 }, bBias: .08, rockSlope: .34, snowLine: 0, verge: .55, ragged: .12, gravel: '#8d8b86', loam: '#4b4743', detail: 1.8, tint: '#ffffff', dry: 0.55 },
  meadow: { a: { tex: 'forrest_ground_01', color: '#5f7d32', rough: .93, scale: 4, tint: '#d8f7b0', gain: 1.05 }, b: { tex: 'forest_leaves_02', color: '#6d7a3a', rough: .93, scale: 3.6, tint: '#e8f2c4' }, rock: { tex: 'rock_face', color: '#8c8a80', rough: .85, scale: 9 }, track: { tex: 'gravel_floor', color: '#a69a80', rough: .93, scale: 3.6 }, bBias: -.1, rockSlope: .38, snowLine: 0, verge: .7, ragged: 1, gravel: '#b5ad9c', loam: '#6b5a44', detail: 1.4, tint: '#ffffff', dry: 0.22 },
  harbour: { a: { tex: 'forrest_ground_01', color: '#647c45', rough: .93, scale: 4, tint: '#dff5c0' }, b: { tex: 'gravel_floor', color: '#8e8a7c', rough: .92, scale: 3.2 }, rock: { tex: 'rock_face', color: '#8e8c86', rough: .85, scale: 9 }, track: { tex: 'clean_asphalt', color: '#44474b', rough: .76, scale: 5.5, gain: 2.2 }, bBias: -.14, rockSlope: .36, snowLine: 0, verge: .45, ragged: .1, gravel: '#8d8b86', loam: '#4b4743', detail: .6, tint: '#ffffff', dry: 0.3 },
  park: { a: { tex: 'forrest_ground_01', color: '#6d8040', rough: .93, scale: 4, tint: '#d4f0b4' }, b: { tex: 'dirt_floor', color: '#9b7a56', rough: .94, scale: 3.6, tint: '#c7b8a8' }, rock: { tex: 'rock_face', color: '#8f8580', rough: .85, scale: 9 }, track: { tex: 'dirt_floor', color: '#a07650', rough: .94, scale: 4.2, tint: '#a39b95' }, bBias: -.24, rockSlope: .36, snowLine: 0, verge: .85, ragged: .8, gravel: '#bba58a', loam: '#6e5139', detail: 1, tint: '#fff6fb', dry: 0.2 },
};

const VERT_PARS = /* glsl */`
attribute vec3 aTrack; attribute vec2 aDir; attribute vec2 aSurf; attribute float aOcc;
varying vec3 vTqWorld; varying vec3 vTqNormal; varying vec3 vTrack; varying vec2 vDir; varying vec2 vSurf; varying float vOcc;`;
const VERT_MAIN = /* glsl */`
vTqWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz; vTqNormal = normalize( mat3( modelMatrix ) * objectNormal );
vTrack = aTrack; vDir = aDir; vSurf = aSurf; vOcc = aOcc;`;
const FRAG_PARS = /* glsl */`
varying vec3 vTqWorld; varying vec3 vTqNormal; varying vec3 vTrack; varying vec2 vDir; varying vec2 vSurf; varying float vOcc;
uniform sampler2D tAlbA; uniform sampler2D tNorA; uniform sampler2D tAlbB; uniform sampler2D tNorB;
uniform sampler2D tAlbR; uniform sampler2D tNorR; uniform sampler2D tAlbT; uniform sampler2D tNorT; uniform sampler2D tNoise;
uniform vec4 uScale; uniform vec4 uTrackP; uniform vec4 uRule; uniform vec3 uGravel; uniform vec3 uLoam; uniform vec3 uTint;
uniform vec3 uTintA; uniform vec3 uTintB; uniform vec3 uTintR; uniform vec3 uTintT; uniform float uDry;
struct TqL { vec3 c; float r; vec2 n; };
TqL tqSample( sampler2D alb, sampler2D nor, vec2 uv ) { vec4 a = texture2D( alb, uv ); vec3 n = texture2D( nor, uv ).xyz * 2.0 - 1.0; return TqL( a.rgb, a.a, vec2( n.x, - n.y ) ); }
TqL tqMix( TqL a, TqL b, float t ) { return TqL( mix( a.c, b.c, t ), mix( a.r, b.r, t ), mix( a.n, b.n, t ) ); }
TqL tqTiled( sampler2D alb, sampler2D nor, vec2 uv, float m ) {
  TqL a = tqSample( alb, nor, uv );
#ifdef TQ_ANTITILE
  TqL b = tqSample( alb, nor, mat2( 0.8, 0.6, -0.6, 0.8 ) * uv * 0.37 + vec2( 0.31, 0.73 ) );
  a = tqMix( a, b, smoothstep( 0.35, 0.65, m ) * 0.55 );
#endif
  return a;
}
float tqLuma( vec3 c ) { return dot( c, vec3( 0.299, 0.587, 0.114 ) ); }
float tqBlend( float w, float ha, float hb ) { return smoothstep( 0.0, 1.0, clamp( w + ( hb - ha ) * 0.9 * min( w, 1.0 - w ) * 2.0, 0.0, 1.0 ) ); }
vec3 tqAlbedo; float tqRough; vec3 tqNormalW;`;
const FRAG_MAIN = /* glsl */`
{
  vec3 geoN = normalize( vTqNormal );
  vec2 wp = vTqWorld.xz;
  // Height feeds the variation lookups too, so cliffs and peaks do not show vertical streaks.
  vec2 wv = wp + vTqWorld.y * vec2( 0.73, 0.41 );
  vec4 nz = texture2D( tNoise, wv * 0.0035 ); vec4 nm = texture2D( tNoise, wv * 0.019 ); vec4 nf = texture2D( tNoise, wv * 0.09 );
  float slope = 1.0 - geoN.y;
  TqL ga = tqTiled( tAlbA, tNorA, wp / uScale.x, nm.r );
#ifdef TQ_SIMPLE
  TqL gb = ga; ga.c *= uTintA; gb.c *= uTintB;
  TqL s = ga; s.c = mix( s.c, s.c * uTintB / max( uTintA, vec3( 0.05 ) ), smoothstep( 0.4, 0.6, nz.r ) * 0.5 );
#else
  TqL gb = tqTiled( tAlbB, tNorB, wp / uScale.y, nm.g );
  ga.c *= uTintA; gb.c *= uTintB;
  float wB = smoothstep( 0.38, 0.62, nz.r * 0.55 + nm.g * 0.45 + uRule.x );
  TqL s = tqMix( ga, gb, tqBlend( wB, tqLuma( ga.c ), tqLuma( gb.c ) ) );
#endif
  vec3 Tn = vec3( 1.0, 0.0, 0.0 ), Bn = vec3( 0.0, 0.0, 1.0 );
  vec3 pert = Tn * s.n.x + Bn * s.n.y;
  // Rock on steep ground: tri-planar so cliffs and mountain faces do not stretch.
  float wR = smoothstep( uRule.y, uRule.y + 0.14, slope + ( nm.b - 0.5 ) * 0.16 );
#ifdef TQ_SIMPLE
  if ( wR > 0.002 ) { TqL r = tqSample( tAlbR, tNorR, vTqWorld.xz / uScale.z ); r.c *= uTintR; s.c = mix( s.c, r.c, wR ); s.r = mix( s.r, r.r, wR ); }
#else
  if ( wR > 0.002 ) {
    vec3 bw = pow( abs( geoN ), vec3( 4.0 ) ); bw /= ( bw.x + bw.y + bw.z );
    TqL rx = tqSample( tAlbR, tNorR, vTqWorld.zy / uScale.z ), ry = tqSample( tAlbR, tNorR, vTqWorld.xz / uScale.z ), rz = tqSample( tAlbR, tNorR, vTqWorld.xy / uScale.z );
    TqL r = TqL( ( rx.c * bw.x + ry.c * bw.y + rz.c * bw.z ) * uTintR, rx.r * bw.x + ry.r * bw.y + rz.r * bw.z, vec2( 0.0 ) );
    vec3 rp = vec3( 0.0, rx.n.y, rx.n.x ) * bw.x + vec3( ry.n.x, 0.0, ry.n.y ) * bw.y + vec3( rz.n.x, rz.n.y, 0.0 ) * bw.z;
    float t = tqBlend( wR, tqLuma( s.c ), tqLuma( r.c ) + 0.1 );
    s.c = mix( s.c, r.c, t ); s.r = mix( s.r, r.r, t ); pert = mix( pert, rp, t );
  }
#endif
  if ( uRule.z > 0.0 ) {
    float wS = smoothstep( uRule.z, uRule.z + 90.0, vTqWorld.y + ( nm.r - 0.5 ) * 160.0 ) * ( 1.0 - smoothstep( 0.42, 0.7, slope ) );
    s.c = mix( s.c, vec3( 0.86, 0.89, 0.93 ) * ( 0.92 + nf.b * 0.12 ), wS ); s.r = mix( s.r, 0.55, wS ); pert *= 1.0 - wS * 0.7;
  }
#ifdef TQ_TRACK
  float lat = abs( vTrack.y ), halfW = uTrackP.x;
  float edgeN = ( texture2D( tNoise, vec2( vTrack.x * 0.045, vTrack.y * 0.06 ) ).b - 0.5 ) * 2.0;
  float wT = 1.0 - smoothstep( halfW - uTrackP.z + edgeN * uTrackP.y, halfW + uTrackP.z + edgeN * uTrackP.y, lat );
  float wV = ( 1.0 - smoothstep( halfW + 0.2, halfW + 2.4 + edgeN * 1.4, lat ) ) * uRule.w;
  vec2 tuv = vec2( vTrack.y, vTrack.x ) / uScale.w;
  TqL tr = tqTiled( tAlbT, tNorT, tuv, nm.b ); tr.c *= uTintT;
  tr.c *= mix( vec3( 1.0 ), uGravel, vSurf.x ); tr.c *= mix( vec3( 1.0 ), uLoam, vSurf.y );
  // Ridden line: slightly darker and smoother where tyres compact the surface.
  float worn = ( 1.0 - smoothstep( 0.4, halfW * 0.7, lat ) ) * ( 1.0 - uTrackP.w );
  tr.c *= 1.0 - worn * 0.1; tr.r = mix( tr.r, tr.r * 0.85, worn );
  if ( uTrackP.w > 0.5 ) {
    float fw = max( fwidth( vTrack.y ), 0.002 );
    float edgeLine = 1.0 - smoothstep( 0.055 - fw, 0.055 + fw, abs( lat - ( halfW - 0.38 ) ) );
    float dash = step( fract( vTrack.x / 11.0 ), 0.42 ) * ( 1.0 - smoothstep( 0.06 - fw, 0.06 + fw, lat ) );
    float paint = max( edgeLine, dash ) * ( 0.75 + nf.r * 0.25 );
    tr.c = mix( tr.c, vec3( 0.78, 0.77, 0.72 ), paint ); tr.r = mix( tr.r, 0.62, paint ); tr.n *= 1.0 - paint * 0.6;
  }
  vec3 right = vec3( - vDir.y, 0.0, vDir.x ), fwd = vec3( vDir.x, 0.0, vDir.y );
  vec3 tp = right * tr.n.x + fwd * tr.n.y;
  // Verge: trampled, mixed with the secondary ground layer.
  s.c = mix( s.c, mix( gb.c, tr.c, 0.45 ), wV * 0.8 ); s.r = mix( s.r, gb.r, wV * 0.5 );
  float tb = tqBlend( wT, tqLuma( s.c ), tqLuma( tr.c ) );
  s.c = mix( s.c, tr.c, tb ); s.r = mix( s.r, tr.r, tb ); pert = mix( pert, tp, tb );
  // Wet tarmac: darker, mirror-like, with puddles in the low spots of the noise.
  float wet = vTrack.z * ( 0.65 + 0.35 * smoothstep( 0.35, 0.7, nm.a ) );
  s.c *= mix( 1.0, 0.52, wet ); s.r = mix( s.r, 0.08, wet * 0.92 ); pert *= 1.0 - wet * 0.75;
#endif
  // Macro variation breaks up tiling over hundreds of metres.
  s.c *= ( 0.86 + nz.g * 0.26 ) * uTint;
  // Sun-dried patches: a broad hue shift so open grassland never looks like one flat carpet.
  float dry = smoothstep( 0.45, 0.75, nz.b * 0.7 + nm.r * 0.3 ) * uDry;
  s.c = mix( s.c, s.c * vec3( 1.18, 1.0, 0.72 ), dry * ( 1.0 - wR ) );
  s.c *= mix( 0.5, 1.0, vOcc );
  tqAlbedo = s.c; tqRough = clamp( s.r, 0.04, 1.0 );
  tqNormalW = normalize( geoN + pert * 0.9 );
}
diffuseColor.rgb *= tqAlbedo;`;

function configure(material: T.MeshStandardMaterial, uniforms: Record<string, T.IUniform>, track: boolean, antitile: boolean) {
  material.defines = { ...(material.defines ?? {}), ...(track ? { TQ_TRACK: '' } : {}), ...(antitile ? { TQ_ANTITILE: '' } : { TQ_SIMPLE: '' }) };
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader.replace('#include <common>', `#include <common>\n${VERT_PARS}`).replace('#include <project_vertex>', `#include <project_vertex>\n${VERT_MAIN}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_PARS}`)
      .replace('#include <map_fragment>', FRAG_MAIN)
      .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = tqRough;')
      .replace('#include <normal_fragment_maps>', 'normal = normalize( ( viewMatrix * vec4( tqNormalW, 0.0 ) ).xyz );');
  };
  material.customProgramCacheKey = () => `tq-terrain-${track}-${antitile}`;
}

export class Terrain {
  group = new T.Group(); field: TrackField; bandHalf = 42;
  private noise = makeNoise(1); private spec: GroundSpec; private uniforms: Record<string, T.IUniform>;
  private core = { x0: 0, x1: 0, z0: 0, z1: 0 }; private materials: T.MeshStandardMaterial[] = [];
  private proj: Projection = { d: 0, l: 0, dist: 0, y: 0, tx: 0, tz: 1, interior: true };
  readonly waterLevel: number | null;
  /** Resolves when the photographic layers have loaded (or failed over to procedural ones). */
  ready: Promise<void>;
  constructor(readonly track: RideTrack, course: RideCourse, private quality: Quality, anisotropy: number) {
    this.field = new TrackField(track, course);
    this.noise = makeNoise(track.seed);
    this.spec = GROUNDS[track.biome];
    const margin = 380; const f = this.field;
    this.core = { x0: f.minX - margin, x1: f.maxX + margin, z0: f.minZ - margin, z1: f.maxZ + margin };
    this.waterLevel = track.biome === 'coast' ? -5 : track.biome === 'harbour' ? 14 : null;
    const s = this.spec, fallback = (l: LayerSpec, seed: number, grain: number) => proceduralLayer(l.color, l.rough, seed, grain);
    const layers = { A: fallback(s.a, 11, 1), B: fallback(s.b, 23, 1.2), R: fallback(s.rock, 37, 1.6), T: fallback(s.track, 51, track.discipline === 'road' ? .5 : 1.3) };
    this.uniforms = {
      tAlbA: { value: layers.A.albedo }, tNorA: { value: layers.A.normal }, tAlbB: { value: layers.B.albedo }, tNorB: { value: layers.B.normal },
      tAlbR: { value: layers.R.albedo }, tNorR: { value: layers.R.normal }, tAlbT: { value: layers.T.albedo }, tNorT: { value: layers.T.normal },
      tNoise: { value: noiseTexture(256, track.seed) },
      uScale: { value: new T.Vector4(s.a.scale, s.b.scale, s.rock.scale, s.track.scale) },
      uTrackP: { value: new T.Vector4(track.width / 2, s.ragged * .45, track.discipline === 'road' ? .06 : .35, track.discipline === 'road' ? 1 : 0) },
      uRule: { value: new T.Vector4(s.bBias, s.rockSlope, s.snowLine, s.verge) },
      uGravel: { value: ratio(s.gravel, s.track.color) },
      uLoam: { value: ratio(s.loam, s.track.color) },
      uTint: { value: new T.Color(s.tint) },
      uDry: { value: s.dry },
      uTintA: { value: new T.Color(1, 1, 1) }, uTintB: { value: new T.Color(1, 1, 1) }, uTintR: { value: new T.Color(1, 1, 1) }, uTintT: { value: new T.Color(1, 1, 1) },
    };
    this.buildFar(); this.buildBand();
    // Photographic layers stream in; procedural stand-ins render until they arrive (or if offline).
    this.ready = Promise.all(([['A', s.a], ['B', s.b], ['R', s.rock], ['T', s.track]] as const).map(([slot, spec]) => loadLayer(spec.tex, anisotropy).then((layer: Layer | null) => {
      if (!layer) return; this.uniforms[`tAlb${slot}`].value = layer.albedo; this.uniforms[`tNor${slot}`].value = layer.normal;
      if (spec.tint || spec.gain) (this.uniforms[`uTint${slot}`].value as T.Color).set(spec.tint ?? '#ffffff').multiplyScalar(spec.gain ?? 1);
    }))).then(() => undefined);
  }
  /** Natural ground (no track influence), including far mountains or mesas outside the arena core. */
  natural(x: number, z: number, _p?: Projection) {
    const t = this.track, n = this.noise, biome = t.biome;
    const hills = Math.sin(x * .021) * 5 + Math.cos(z * .018) * 4;
    let y = 13 + hills;
    if (t.discipline === 'mtb') {
      // Valley sides: one bank rises, the other falls away, measured from a continuous valley axis.
      const v = this.field.valley(z), lateral = Math.max(-650, Math.min(650, v.x - x));
      y = biome === 'meadow' ? v.y + hills * .28 : v.y + hills + (lateral > 0 ? lateral * .17 : lateral * .14);
    }
    if (biome === 'park') y = 17 + hills * .35;
    if (biome === 'harbour') y = 18 + hills * .2;
    y += (n.fbm(x / 41, z / 41, 4) - .5) * this.spec.detail;
    const c = this.core, dx = Math.max(c.x0 - x, 0, x - c.x1), dz = Math.max(c.z0 - z, 0, z - c.z1), out = Math.hypot(dx, dz);
    const amp = smooth(0, 1100, out), land = biome === 'coast' || biome === 'harbour' ? smooth(this.field.maxX + 160, this.field.maxX - 260, x) : 1;
    if (amp > 0 && land > 0) {
      if (biome === 'alpine') y += (n.ridged(x / 820, z / 820, 6) * 980 + n.ridged(x / 240, z / 240, 4) * 150) * amp * smooth(-200, 600, out);
      else if (biome === 'canyon') {
        const h = Math.max(0, n.fbm(x / 640, z / 640, 5) - .43) * 1500, step = 44, k = h / step, base = Math.floor(k);
        y += (base + smooth(.72, 1, k - base)) * step * amp + n.fbm(x / 90, z / 90, 3) * 14 * amp;
      } else if (biome === 'meadow') y += (n.fbm(x / 520, z / 520, 5) * 170 + n.ridged(x / 1100, z / 1100, 5) * 620 * smooth(900, 2400, out)) * amp;
      else if (biome === 'park') y += (n.fbm(x / 470, z / 470, 5) * 260 + n.ridged(x / 900, z / 900, 4) * 280 * smooth(700, 2200, out)) * amp;
      else y += (n.fbm(x / 600, z / 600, 5) * 380 + n.ridged(x / 750, z / 750, 4) * 220) * amp * land;
    }
    if (biome === 'coast' && x > this.field.maxX + 12) y = Math.min(y, 4 - (x - this.field.maxX - 12) * .25);
    if (biome === 'harbour' && x > this.field.maxX + 12) y = Math.min(y, 12 - (x - this.field.maxX - 12) * .6);
    return y;
  }
  /** Ground under the riding band: track surface, verge, then a blend into natural ground. */
  private bandHeight(d: number, l: number, trackY: number, natural: number) {
    const a = Math.abs(l), hw = this.track.width / 2, L = this.track.length;
    let y = trackY - (this.track.discipline === 'road' ? .06 : .03) * Math.min(1, (a / hw) ** 2);
    if (a > hw) { y = trackY - .06 - .1 * smooth(hw, hw + 1.1, a); y += (natural - y) * smooth(hw + 1.1, hw + 27, a); }
    if (!this.field.closed) { const e = d < 0 ? smooth(-12, -this.field.ext, d) : smooth(L + 24, L + this.field.ext, d); y += (natural - y) * e; }
    return y;
  }
  /** Visible ground height at a world point (matches the meshes). */
  heightAt(x: number, z: number) {
    const p = this.field.project(x, z, this.proj), natural = this.natural(x, z, p);
    if (p.interior && p.dist < this.bandHalf) return this.bandHeight(p.d, p.l, p.y, natural);
    return natural;
  }
  private buildFar() {
    const step = this.quality.terrainStep, c = this.core, outer = this.quality.level === "low" ? 24 : 44, growth = this.quality.level === "low" ? 1.25 : 1.115;
    const axis = (a0: number, a1: number) => {
      const n = Math.ceil((a1 - a0) / step), core = Array.from({ length: n + 1 }, (_, i) => a0 + (a1 - a0) * i / n);
      const lo: number[] = [], hi: number[] = []; let s = step, pa = a0, pb = a1;
      for (let i = 0; i < outer; i++) { s *= growth; pa -= s; pb += s; lo.unshift(pa); hi.push(pb); }
      return [...lo, ...core, ...hi];
    };
    const xs = axis(c.x0, c.x1), zs = axis(c.z0, c.z1), nx = xs.length, nz = zs.length;
    const pos = new Float32Array(nx * nz * 3), track = new Float32Array(nx * nz * 3), dir = new Float32Array(nx * nz * 2), occ = new Float32Array(nx * nz).fill(1);
    const p = this.proj;
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      const x = xs[i], z = zs[j], k = j * nx + i;
      this.field.project(x, z, p);
      const natural = this.natural(x, z, p);
      let y = natural;
      if (p.interior && p.dist < this.bandHalf) y = this.bandHeight(p.d, p.l, p.y, natural) - .7 * (1 - smooth(this.bandHalf - 7, this.bandHalf - .5, p.dist));
      pos[k * 3] = x; pos[k * 3 + 1] = y; pos[k * 3 + 2] = z;
      track[k * 3] = 0; track[k * 3 + 1] = 1e4; dir[k * 2] = 1;
    }
    const index = new Uint32Array((nx - 1) * (nz - 1) * 6); let w = 0;
    for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) {
      const a = j * nx + i, b = a + 1, d = a + nx, e = d + 1;
      index[w++] = a; index[w++] = d; index[w++] = b; index[w++] = b; index[w++] = d; index[w++] = e;
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3)); geo.setAttribute('aTrack', new T.BufferAttribute(track, 3)); geo.setAttribute('aDir', new T.BufferAttribute(dir, 2));
    geo.setAttribute('aSurf', new T.BufferAttribute(new Float32Array(nx * nz * 2), 2)); geo.setAttribute('aOcc', new T.BufferAttribute(occ, 1));
    geo.setIndex(new T.BufferAttribute(index, 1)); geo.computeVertexNormals(); geo.computeBoundingSphere();
    const material = new T.MeshStandardMaterial({ color: '#ffffff', roughness: 1, metalness: 0 });
    configure(material, this.uniforms, false, this.quality.level !== 'low'); this.materials.push(material);
    // Distant ground only needs shadows in the richer tiers; the band under the riders always receives them.
    const mesh = new T.Mesh(geo, material); mesh.name = 'terrain-far'; mesh.receiveShadow = this.quality.level !== 'low'; this.group.add(mesh);
    mesh.userData.grid = { xs, zs };
  }
  private buildBand() {
    const t = this.track, hw = t.width / 2, step = this.quality.bandStep, f = this.field, L = t.length;
    const inner = [0, .25, .5, .75, 1].map(v => v * hw), outerOffsets = [.35, .8, 1.4, 2.2, 3.2, 4.5, 6, 8, 10.5, 13.5, 17, 21, 26, 32, this.bandHalf - hw];
    const half = [...inner, ...outerOffsets.map(v => hw + v)];
    const lats = [...half.slice(1).map(v => -v).reverse(), ...half];
    const d0 = -f.ext, d1 = L + f.ext, rows = Math.ceil((d1 - d0) / step) + 1, cols = lats.length;
    const pos = new Float32Array(rows * cols * 3), track = new Float32Array(rows * cols * 3), dir = new Float32Array(rows * cols * 2), surf = new Float32Array(rows * cols * 2), occ = new Float32Array(rows * cols).fill(1);
    const p = this.proj;
    for (let r = 0; r < rows; r++) {
      const d = Math.min(d1, d0 + r * step), c = f.center(d);
      // Surface type averaged over ±8 m so changes fade along the trail.
      let gravel = 0, loam = 0, wet = 0;
      if (d >= 0 && d <= L) for (let k = -2; k <= 2; k++) { const s = surfaceAt(t, Math.min(1, Math.max(0, (d + k * 4) / L))); gravel += +(s === 'gravel') / 5; loam += +(s === 'loam') / 5; wet += +(s === 'wet') / 5; }
      for (let q = 0; q < cols; q++) {
        const l = lats[q], x = c.x - c.tz * l, z = c.z + c.tx * l, k = r * cols + q;
        p.dist = Math.abs(l); p.y = c.y; p.l = l;
        const natural = this.natural(x, z, p);
        pos[k * 3] = x; pos[k * 3 + 1] = this.bandHeight(d, l, c.y, natural); pos[k * 3 + 2] = z;
        track[k * 3] = d; track[k * 3 + 1] = l; track[k * 3 + 2] = wet; dir[k * 2] = c.tx; dir[k * 2 + 1] = c.tz; surf[k * 2] = gravel; surf[k * 2 + 1] = loam;
      }
    }
    const index = new Uint32Array((rows - 1) * (cols - 1) * 6); let w = 0;
    for (let r = 0; r < rows - 1; r++) for (let q = 0; q < cols - 1; q++) {
      const a = r * cols + q, b = a + 1, d = a + cols, e = d + 1;
      index[w++] = a; index[w++] = b; index[w++] = d; index[w++] = b; index[w++] = e; index[w++] = d;
    }
    const geo = new T.BufferGeometry();
    geo.setAttribute('position', new T.BufferAttribute(pos, 3)); geo.setAttribute('aTrack', new T.BufferAttribute(track, 3)); geo.setAttribute('aDir', new T.BufferAttribute(dir, 2));
    geo.setAttribute('aSurf', new T.BufferAttribute(surf, 2)); geo.setAttribute('aOcc', new T.BufferAttribute(occ, 1));
    geo.setIndex(new T.BufferAttribute(index, 1)); geo.computeVertexNormals(); geo.computeBoundingSphere();
    const n = geo.attributes.normal as T.BufferAttribute;
    if (n.getY(0) < 0) { for (let i = 0; i < index.length; i += 3) { const tmp = index[i + 1]; index[i + 1] = index[i + 2]; index[i + 2] = tmp; } geo.computeVertexNormals(); }
    const material = new T.MeshStandardMaterial({ color: '#ffffff', roughness: 1, metalness: 0, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -2 });
    configure(material, this.uniforms, true, this.quality.level !== 'low'); this.materials.push(material);
    const mesh = new T.Mesh(geo, material); mesh.name = 'terrain-band'; mesh.receiveShadow = true; this.group.add(mesh);
  }
  /** Darkens ground beneath trees and boulders, a cheap stand-in for distant contact shadows. */
  setOcclusion(points: { x: number; z: number; r: number }[]) {
    const cell = 24, grid = new Map<number, { x: number; z: number; r: number }[]>();
    for (const o of points) { const key = Math.floor(o.x / cell) * 92821 ^ Math.floor(o.z / cell); (grid.get(key) ?? grid.set(key, []).get(key)!).push(o); }
    for (const mesh of this.group.children as T.Mesh[]) {
      const pos = mesh.geometry.attributes.position as T.BufferAttribute, occ = mesh.geometry.attributes.aOcc as T.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), z = pos.getZ(i), cx = Math.floor(x / cell), cz = Math.floor(z / cell); let shade = 1;
        for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) for (const o of grid.get((cx + a) * 92821 ^ (cz + b)) ?? []) {
          const d = Math.hypot(o.x - x, o.z - z) / o.r; if (d < 1.6) shade *= 1 - .55 * (1 - smooth(.15, 1.6, d));
        }
        occ.setX(i, Math.max(.25, shade));
      }
      occ.needsUpdate = true;
    }
  }
  dispose() {
    for (const mesh of this.group.children as T.Mesh[]) mesh.geometry.dispose();
    this.materials.forEach(m => m.dispose());
    for (const u of Object.values(this.uniforms)) if (u.value instanceof T.Texture) u.value.dispose();
  }
}
